import { useState } from 'react';
import { useHandlePreSignEntry, useHandlePreSignEntryPatch, useHandlePreSignUpload, useHandlePreSignUploadAWS } from '../../../mutations';
import importService from '../api/api';

export const useImport = ({ onSuccess, onError }: { onSuccess: () => void; onError: (error: string) => void; }) => {

    const handlePreSignUpload = useHandlePreSignUpload();
    const handlePreSignUploadAWS = useHandlePreSignUploadAWS();
    const handlePreSignEntry = useHandlePreSignEntry();
    const handlePreSignEntryPatch = useHandlePreSignEntryPatch();

    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const handleFileSelect = (file: File | null) => {
        if (!file) {
            setSelectedFile(null);
            setUploadError(null);
            return;
        }
        const allowedTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (allowedTypes.includes(file.type) || fileExtension === 'csv' || fileExtension === 'xlsx') {
            setSelectedFile(file);
            setUploadError(null);
        } else {
            setSelectedFile(null);
            setUploadError('Invalid file type. Please upload a CSV or XLSX file.');
        }
    };

    const uploadFile = async (type: string) => {
        setIsUploading(true);
        setUploadError(null);

        if (!selectedFile) {
            const errorMsg = 'Please select a file to upload';
            setUploadError(errorMsg);
            setIsUploading(false);
            onError(errorMsg);
            return;
        }

        try {
            // Step 1: Create pre-sign entry
            const entryParams = {
                id: null,
                key: null,
                file_uuid: null,
                file_type: type,
                file_name: selectedFile.name,
                file_size: selectedFile.size.toString(),
                mime_type: selectedFile.type.toString(),
            };

            const entryResponse = await handlePreSignEntry.mutateAsync(entryParams);
            
            if (!entryResponse.status) {
                throw new Error('Failed to create pre-sign entry');
            }

            // Step 2: Get pre-signed upload URL
            const presignParams = {
              id: String(entryResponse.data.rows.file_id),
              file_type: type,
              file_name: selectedFile.name,
            };

            const uploadResponse = await handlePreSignUpload.mutateAsync(presignParams);

            if (!uploadResponse.status) {
                throw new Error('Failed to get pre-signed upload URL');
            }

            // Step 3: Upload to AWS S3
            const formData = new FormData();
            Object.keys(uploadResponse.data.url.fields).forEach(key => {
                formData.append(key, uploadResponse.data.url.fields[key]);
            });
            formData.append('file', selectedFile);

            const awsParams = {
                url: uploadResponse.data.url.url,
                fields: formData,
            };

            await handlePreSignUploadAWS.mutateAsync(awsParams);

            // Step 4: Update entry with file UUID
            const patchParams = {
              file_id: entryResponse.data.rows.file_id,
              s3_path: uploadResponse.data.url.fields.key,
              file_uuid: uploadResponse.data.attachmentId,
              // key: uploadResponse.data.url.fields.key,
            };

            const finalResponse = await handlePreSignEntryPatch.mutateAsync(patchParams);

            if (!finalResponse.status) {
                throw new Error('Failed to update file entry');
            }

            // Step 5: Complete import process
            await importService.upload({
                type,
                s3_filekey: uploadResponse.data.url.fields.key,
            });

            // Success - reset state and notify
            setIsUploading(false);
            setSelectedFile(null);
            setUploadError(null);
            onSuccess();

        } catch (err: any) {
            const errorMessage = err.message || 'An error occurred during file upload';
            console.error('Upload error:', err);
            setUploadError(errorMessage);
            setIsUploading(false);
            onError(errorMessage);
        }
    };

    const reset = () => {
        setSelectedFile(null);
        setUploadError(null);
        setIsUploading(false);
    };

    return {
        selectedFile,
        isUploading,
        uploadError,
        handleFileSelect,
        uploadFile,
        reset,
    };

};